import { formatDistanceToNowStrict } from 'date-fns';
import type { RecipientStatus } from '../types';
import { BadgeVariant } from '@/components/core-components/StanBadge';

export * from './filterTransformation.logic';
export * from './apiParams.logic';
export * from './emailRecipientStatus.logic';

/**
 * Get empty state message based on filter state
 */
export const getEmptyStateMessage = (hasFilters: boolean) => {
    const header = hasFilters ? 'No Email Recipients Matching Filters' : 'No Email Recipients Yet';
    const subheader = hasFilters
        ? "Update filters to find what you're looking for!"
        : 'Learn how to send your first email <a href="https://help.stan.store/article/164-getting-started-with-email-flows" target="_blank">here</a>!';

    return { header, subheader };
};

/**
 * Get human-readable status text for a recipient
 */
export const getRecipientStatusText = (status: RecipientStatus): string => {
    switch (status.name) {
        case 'subscribed':
            return 'Subscribed';
        case 'unsubscribed':
            return 'Unsubscribed';
        case 'opt_in_sent':
            if (status.timestamp) {
                return `Opt-In Email Sent ${formatDistanceToNowStrict(status.timestamp * 1000, { addSuffix: true })}`;
            }
            return 'Opt-In Email Sent';
        case 'require_opt_in':
            return 'Requires Opt-In';
        default:
            return 'Unknown';
    }
};

/**
 * Get badge variant for recipient status
 */
export const getRecipientStatusVariant = (status: RecipientStatus): BadgeVariant => {
    switch (status.name) {
        case 'subscribed':
            return 'success';
        case 'unsubscribed':
            return 'destructive';
        case 'opt_in_sent':
            return 'muted';
        case 'require_opt_in':
            return 'primary';
        default:
            return 'default';
    }
};

/**
 * Get pagination label for email recipients table
 * Shows range of results being displayed (e.g., "1-10 of 20+ results")
 * On the last page, shows exact total (e.g., "141-144 of 144 results")
 */
export const getPaginationLabel = (
    currentPageItemCount: number,
    totalPages: number,
    pageSize: number,
    currentPage: number = 1
): string | undefined => {
    if (totalPages === 0) return undefined;

    const startIndex = (currentPage - 1) * pageSize + 1;
    const endIndex = startIndex + currentPageItemCount - 1;

    if (totalPages === 1) {
        return `${currentPageItemCount} result${currentPageItemCount !== 1 ? 's' : ''}`;
    }

    // On the last page, show exact total
    if (currentPage === totalPages) {
        return `Displaying ${startIndex}-${endIndex} of ${endIndex} results`;
    }

    // On other pages, show approximate total with "+"
    const approximateTotalResults = (totalPages - 1) * pageSize;
    return `Displaying ${startIndex}-${endIndex} of ${approximateTotalResults}+ results`;
};
